/**
 * Export Modes
 * Two export strategies: AI-friendly (minimal) vs Full (expanded)
 */

import { Node, Edge } from '@xyflow/react'
import { DiagramData } from '../types/schema'
import { exportForAI, exportFull, type EditTracker } from './serializer'

export type ExportMode = 'ai' | 'full'

export interface ExportConfig {
  mode: ExportMode
  prettify?: boolean
  includeOriginal?: boolean
  includeEditMetadata?: boolean
}

/**
 * Export diagram based on mode
 * Simple wrapper that handles both modes
 */
export function exportDiagram(
  mode: ExportMode,
  originalData: DiagramData,
  currentNodes: Node[],
  currentEdges: Edge[],
  editTracker?: EditTracker,
  config: Partial<ExportConfig> = {}
): string {
  const { prettify = true, includeOriginal = false, includeEditMetadata = true } = config

  if (mode === 'ai') {
    // AI-friendly: minimal JSON + edit tracking
    if (!editTracker) {
      throw new Error('Edit tracker required for AI export mode')
    }
    return exportForAI(originalData, currentNodes, currentEdges, editTracker, {
      prettify,
      includeEditMetadata,
    })
  } else {
    // Full: everything expanded
    return exportFull(originalData, currentNodes, currentEdges, {
      prettify,
      includeOriginal,
    })
  }
}

/**
 * Download exported JSON as file
 */
export function downloadJSON(json: string, filename: string) {
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
